import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { GlobalService } from 'app/core/global.service';
import { RolesService } from 'app/modules/admin/roles-permissions/roles.service';
import { MODULE_PERMISSION_KEYS } from 'app/core/permission/permission.service';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { extractPermissionsFromApiResponse } from 'app/modules/admin/roles-permissions/permission-group.util';
import { ErpPdfService } from 'app/core/pdf/erp-pdf.service';

/** Main module view permission keys - only these are shown for company assignment */
const COMPANY_MODULE_KEYS = [
    MODULE_PERMISSION_KEYS.purchases,
    MODULE_PERMISSION_KEYS.sales,
    MODULE_PERMISSION_KEYS.commission,
    MODULE_PERMISSION_KEYS.commodity,
    MODULE_PERMISSION_KEYS.accounting,
    MODULE_PERMISSION_KEYS.inventory,
    MODULE_PERMISSION_KEYS.hr,
    MODULE_PERMISSION_KEYS.logistics,
    MODULE_PERMISSION_KEYS.admin,
    MODULE_PERMISSION_KEYS.manufacturing,
    MODULE_PERMISSION_KEYS.gate,
    MODULE_PERMISSION_KEYS.checklists,
    'can_edit_company_profile',
] as const;

@Component({
    selector: 'app-edit-company-profile',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        OverlayLoaderDirective,
    ],
    templateUrl: './edit-company-profile.component.html',
})
export class EditCompanyProfileComponent implements OnInit {
    form!: FormGroup;
    loading = false;
    logoPreview: string | null = null;

    permissions: any[] = [];
    permissionsLoading = false;
    /** Master permission id per module key (from permissions API + merged rows) — used when building replace payload */
    private permissionIdByKey = new Map<string, string>();
    /** Resolved from GET CompanyType where type name is "bizcare" (for profile payloads that only send type id). */
    private bizcareCompanyTypeId: string | null = null;
    /** Display labels for main module permissions */
    readonly moduleLabels: Record<string, string> = {
        [MODULE_PERMISSION_KEYS.purchases]: 'View Purchases Module',
        [MODULE_PERMISSION_KEYS.sales]: 'View Sales Module',
        [MODULE_PERMISSION_KEYS.commission]: 'View Sales Commission Module',
        [MODULE_PERMISSION_KEYS.commodity]: 'View Commodity Module',
        [MODULE_PERMISSION_KEYS.accounting]: 'View Accounting Module',
        [MODULE_PERMISSION_KEYS.inventory]: 'View Inventory Module',
        [MODULE_PERMISSION_KEYS.hr]: 'View HR Module',
        [MODULE_PERMISSION_KEYS.logistics]: 'View Logistics Module',
        [MODULE_PERMISSION_KEYS.admin]: 'View Administration Module',
        [MODULE_PERMISSION_KEYS.manufacturing]: 'View Manufacturing Module',
        [MODULE_PERMISSION_KEYS.gate]: 'View Gate Module',
        [MODULE_PERMISSION_KEYS.checklists]: 'View Checklists Module',
        'can_edit_company_profile': 'Edit Company Profile',
    };

    constructor(
        private fb: FormBuilder,
        private _authService: AuthService,
        private _userService: UserService,
        private _globalService: GlobalService,
        private _rolesService: RolesService,
        private _toast: ToastrService,
        private _erpPdf: ErpPdfService,
        @Optional() private _dialogRef?: MatDialogRef<EditCompanyProfileComponent>,
    ) {
        this.buildForm();
    }

    close(): void {
        this._dialogRef?.close();
    }

    ngOnInit(): void {
        this.patchFormFromCompany();
        if (this.showCompanyPermissions) {
            this.loadPermissions();
        }
        lastValueFrom(this._globalService.getCompanyTypes())
            .then((resp: any) => {
                const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.Data) ? resp.Data : [];
                const match = list.find(
                    (t: any) => String(t?.name ?? t?.Name ?? '').trim().toLowerCase() === 'bizcare'
                );
                const id = match?.id ?? match?.Id;
                this.bizcareCompanyTypeId = id != null && id !== '' ? String(id) : null;
            })
            .catch(() => {
                this.bizcareCompanyTypeId = null;
            });
    }

    private buildForm(): void {
        this.form = this.fb.group({
            name: [{ value: null, disabled: true }],
            companyCode: [{ value: null, disabled: true }, Validators.required],
            logo: [null],
            email: [{ value: null, disabled: true }],
            phone: [null],
            website: [null],
            country: [null],
            city: [null],
            address: [null],
            timezone: [{ value: null, disabled: true }],
            /** Lowercase keys from COMPANY_MODULE_KEYS — source of truth for checked state (works even when row has no id yet) */
            selectedPermissionKeys: [[] as string[]],
        });
    }

    private patchFormFromCompany(): void {
        const company = this._authService.profile()?.company;
        if (!company) return;
        const c = company;
        this.form.patchValue({
            name: c.name ?? c.companyName ?? null,
            companyCode: c.companyCode ?? c.code ?? '',
            logo: c.logo ?? c.logoUrl ?? null,
            email: c.email ?? null,
            phone: c.phone ?? null,
            website: c.website ?? null,
            country: c.country ?? null,
            city: c.city ?? null,
            address: c.address ?? null,
            timezone: c.timezone ?? null,
        });
        const logoVal = c.logo ?? c.logoUrl;
        if (logoVal && typeof logoVal === 'string') {
            this.logoPreview = logoVal.startsWith('http')
                ? logoVal
                : `${environment.apiUrl.replace(/\/api\/?$/, '')}/${logoVal.replace(/^\//, '')}`;
        }
    }

    /** Normalize permission key from API (camelCase or PascalCase). */
    private permissionKeyNorm(p: any): string {
        return String(
            p?.key ??
                p?.Key ??
                p?.code ??
                p?.Code ??
                p?.permissionKey ??
                p?.PermissionKey ??
                p?.permissionCode ??
                p?.PermissionCode ??
                ''
        )
            .toLowerCase()
            .trim();
    }

    /** Resolve permission id — template must not use `perm.id` alone (API often sends `Id`). */
    private permissionRecordId(p: any): string | null {
        const raw =
            p?.id ??
            p?.Id ??
            p?.permissionId ??
            p?.PermissionId ??
            p?.permissionID ??
            p?.uuid ??
            p?.Uuid;
        if (raw == null || raw === '') return null;
        return String(raw);
    }

    private withResolvedPermission(p: any): any {
        const rid = this.permissionRecordId(p);
        return rid ? { ...p, id: rid } : { ...p };
    }

    private extractPermissionList(resp: any): any[] {
        return extractPermissionsFromApiResponse(resp);
    }

    private moduleNameNorm(p: any): string {
        return String(p?.module ?? p?.Module ?? '').toLowerCase().trim();
    }

    /** If catalog has no `can_view_inventory` row, map the best Inventory-module gate by key/name. */
    private applyInventoryCatalogFallback(keyToPerm: Map<string, any>, allList: any[]): void {
        const inv = MODULE_PERMISSION_KEYS.inventory.toLowerCase();
        if (keyToPerm.has(inv)) return;

        const score = (k: string): number => {
            if (k === 'can_view_inventory') return 0;
            if (k.includes('can_view') && k.includes('inventory')) return 1;
            if (k.includes('can_view')) return 2;
            if (k.includes('inventory') && k.includes('view')) return 3;
            if (k.includes('inventory') && (k.includes('dashboard') || k.includes('module'))) return 4;
            return 9;
        };

        let picked = allList
            .map((p) => this.withResolvedPermission(p))
            .filter((p) => !!this.permissionRecordId(p))
            .filter((p) => {
                if (this.moduleNameNorm(p) !== 'inventory') return false;
                const k = this.permissionKeyNorm(p);
                const n = String(p?.name ?? p?.Name ?? '').toLowerCase();
                if (k === 'can_view_inventory') return true;
                if (k.includes('inventory') && k.includes('view')) return true;
                if (k.includes('can_view') && k.includes('inventory')) return true;
                if (k.includes('inventory') && (k.includes('dashboard') || k.includes('module'))) return true;
                if (n.includes('inventory') && (n.includes('view') || n.includes('module'))) return true;
                return false;
            });

        if (picked.length === 0) {
            picked = allList
                .map((p) => this.withResolvedPermission(p))
                .filter((p) => !!this.permissionRecordId(p))
                .filter((p) => {
                    const k = this.permissionKeyNorm(p);
                    if (k === 'can_view_inventory') return true;
                    return k.includes('inventory') && (k.includes('view') || k.includes('can_view'));
                });
        }

        if (picked.length === 0) return;

        picked.sort((a, b) => score(this.permissionKeyNorm(a)) - score(this.permissionKeyNorm(b)));
        const pick = picked[0];
        keyToPerm.set(
            inv,
            this.withResolvedPermission({
                ...pick,
                key: MODULE_PERMISSION_KEYS.inventory,
            })
        );
    }

    /**
     * Company-permissions API: pre-check only when the assignment row is explicitly active.
     * Missing from API, or isActive false/0, or omitted isActive → not pre-checked (still shown; user can toggle for replace).
     */
    private isCompanyAssignmentExplicitlyActive(a: any): boolean {
        return a?.isActive === true || a?.isActive === 1;
    }

    /** Load all pages from GET /api/permissions (catalog may be paginated). */
    private fetchAllPermissionsPages(): Promise<any[]> {
        const pageSize = 500;
        const acc: any[] = [];
        const fetchPage = (page: number): Promise<any[]> =>
            lastValueFrom(this._rolesService.getAllPermissions(page, pageSize)).then((resp: any) => {
                const list = this.extractPermissionList(resp);
                acc.push(...list);
                if (list.length >= pageSize) return fetchPage(page + 1);
                return acc;
            });
        return fetchPage(1);
    }

    /** Prefer unpaged GET like role editor; fall back to paging if list is incomplete. */
    private fetchAllPermissionsCatalog(): Promise<any[]> {
        return lastValueFrom(this._rolesService.getAllPermissions())
            .then((resp: any) => {
                const list = this.extractPermissionList(resp);
                const total = Number(resp?.meta?.totalRecords ?? resp?.Meta?.totalRecords ?? 0);
                if (list.length === 0) return this.fetchAllPermissionsPages();
                if (total > 0 && list.length < total) return this.fetchAllPermissionsPages();
                return list;
            })
            .catch(() => this.fetchAllPermissionsPages());
    }

    private extractAssignedList(assignedResp: any): any[] {
        const assignedData = assignedResp?.data ?? assignedResp?.Data ?? assignedResp;
        if (Array.isArray(assignedData)) return assignedData;
        const inner = assignedData?.items ?? assignedData?.Items ?? assignedData?.data ?? assignedData?.Data;
        return Array.isArray(inner) ? inner : [];
    }

    /** company-permissions rows keyed by normalized permission key (only the 9 company-module keys). */
    private indexCompanyAssignmentsByKey(assignedList: any[]): Map<string, any> {
        const byKey = new Map<string, any>();
        for (const a of assignedList) {
            const perm = a?.permission;
            const rootKey = String(a?.permissionKey ?? a?.PermissionKey ?? '').toLowerCase().trim();
            const permKey = this.permissionKeyNorm(perm);
            const k = rootKey || permKey;
            if (!k) continue;
            if (!COMPANY_MODULE_KEYS.some((wanted) => wanted.toLowerCase() === k)) continue;
            byKey.set(k, a);
        }
        return byKey;
    }

    /**
     * 1) Load full permissions catalog (GET permissions).
     * 2) Show exactly the 9 module rows (fixed order), resolved from catalog by key (+ inventory fallback + merge from company-permissions when catalog omits a key).
     * 3) Load company-permissions; pre-check a row only if that key has an assignment with isActive true/1.
     *    Not in company response or inactive → unchecked, still editable for replace payload (master permission id on the row).
     */
    loadPermissions(): void {
        if (this.permissions.length > 0) return;
        this.permissionsLoading = true;
        Promise.all([
            this.fetchAllPermissionsCatalog(),
            lastValueFrom(this._globalService.getCompanyAssignedPermissions()).catch(() => ({ data: [] })),
        ]).then(([allList, assignedResp]: [any[], any]) => {
            const keyToPerm = new Map<string, any>();

            this.permissionIdByKey = new Map<string, string>();
            const registerCatalogId = (k: string, id: string | null): void => {
                if (!k || !id) return;
                if (!COMPANY_MODULE_KEYS.some((wanted) => wanted.toLowerCase() === k)) return;
                this.permissionIdByKey.set(k, id);
            };

            for (const p of allList) {
                const k = this.permissionKeyNorm(p);
                if (k) keyToPerm.set(k, this.withResolvedPermission(p));
                const id = this.permissionRecordId(this.withResolvedPermission(p));
                registerCatalogId(k, id);
            }

            this.applyInventoryCatalogFallback(keyToPerm, allList);

            const assignedList = this.extractAssignedList(assignedResp);
            const assignmentsByKey = this.indexCompanyAssignmentsByKey(assignedList);

            for (const a of assignedList) {
                const perm = a?.permission;
                const pid =
                    this.permissionRecordId(perm) ??
                    this.permissionRecordId(a);
                const rootKey = String(a?.permissionKey ?? a?.PermissionKey ?? '').toLowerCase().trim();
                const permKey = this.permissionKeyNorm(perm);
                const k = rootKey || permKey;
                if (!k || !pid) continue;
                if (!COMPANY_MODULE_KEYS.some((wanted) => wanted.toLowerCase() === k)) continue;
                if (!keyToPerm.has(k)) {
                    keyToPerm.set(
                        k,
                        this.withResolvedPermission({
                            ...perm,
                            id: this.permissionRecordId(perm) ?? pid,
                            key:
                                perm?.key ??
                                perm?.Key ??
                                a.permissionKey ??
                                a?.PermissionKey ??
                                perm?.code ??
                                perm?.Code,
                        })
                    );
                }
            }

            this.permissions = COMPANY_MODULE_KEYS.map((wanted) => {
                const k = wanted.toLowerCase();
                const found = keyToPerm.get(k);
                if (found) return this.withResolvedPermission(found);
                return {
                    key: wanted,
                    id: null,
                    name: this.moduleLabels[wanted] ?? wanted,
                };
            });

            for (const row of this.permissions) {
                const rk = this.rowPermissionKey(row);
                const id = this.permissionRecordId(row);
                registerCatalogId(rk, id);
            }

            const initialKeys: string[] = [];
            for (const wanted of COMPANY_MODULE_KEYS) {
                const k = wanted.toLowerCase();
                const assignment = assignmentsByKey.get(k);
                if (assignment && this.isCompanyAssignmentExplicitlyActive(assignment)) {
                    initialKeys.push(k);
                }
            }
            this.form.get('selectedPermissionKeys')?.setValue(initialKeys);
        }).catch(() => {
            this.permissions = [];
        }).finally(() => {
            this.permissionsLoading = false;
        });
    }

    /** Stable @for track when some rows are synthetic (no catalog id yet). */
    trackPermissionRow(_index: number, perm: any): string {
        const rid = this.permissionRecordId(perm);
        if (rid) return rid;
        return String(perm?.key ?? perm?.Key ?? perm?.code ?? perm?.Code ?? _index);
    }

    rowPermissionKey(perm: any): string {
        return String(perm?.key ?? perm?.Key ?? perm?.code ?? perm?.Code ?? '')
            .toLowerCase()
            .trim();
    }

    private resolvePermissionIdForKey(key: string): string | null {
        const k = key.toLowerCase().trim();
        if (!k) return null;
        return this.permissionIdByKey.get(k) ?? null;
    }

    getPermissionLabel(perm: any): string {
        const key = perm?.key ?? perm?.Key ?? perm?.code ?? perm?.Code ?? '';
        return this.moduleLabels[key] ?? perm?.name ?? perm?.Name ?? key;
    }

    /** Company Permissions card is only shown for BizCare (platform operator) companies. */
    get showCompanyPermissions(): boolean {
        return this.isBizcareCompanyType();
    }

    /** False when company type is BizCare (platform operator); those companies have read-only module permissions. */
    get canEditPermissions(): boolean {
        return !this.isBizcareCompanyType();
    }

    private isBizcareCompanyType(): boolean {
        if (this._authService.isBizcareCompanyType()) return true;

        const c = this._authService.profile()?.company as any;
        if (!c) return false;

        const typeId =
            c.companyTypeId ??
            c.CompanyTypeId ??
            c.companyType?.id ??
            c.CompanyType?.id ??
            c.CompanyType?.Id;
        return !!(this.bizcareCompanyTypeId && typeId != null && String(typeId) === this.bizcareCompanyTypeId);
    }

    isModulePermissionChecked(perm: any): boolean {
        const key = this.rowPermissionKey(perm);
        if (!key) return false;
        const keys = (this.form.get('selectedPermissionKeys')?.value as string[]) ?? [];
        return keys.some((k) => k.toLowerCase() === key);
    }

    toggleModulePermission(perm: any, checked: boolean): void {
        const key = this.rowPermissionKey(perm);
        if (!key) return;
        const ctrl = this.form.get('selectedPermissionKeys');
        const keys = [...((ctrl?.value as string[]) ?? [])];
        const k = key.toLowerCase();
        let next: string[];
        if (checked) {
            next = keys.some((x) => x.toLowerCase() === k) ? keys : [...keys, k];
        } else {
            next = keys.filter((x) => x.toLowerCase() !== k);
        }
        ctrl?.setValue(next);
        ctrl?.markAsDirty();
    }

    onLogoChange(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            this.logoPreview = reader.result as string;
        };
        reader.readAsDataURL(file);
        lastValueFrom(this._globalService.uploadFile(file, 'CompanyLogo')).then((resp: any) => {
            if (resp.success && resp?.data?.fileUrl) {
                this.form.patchValue({ logo: resp.data.fileUrl });
            } else {
                this.form.patchValue({ logo: null });
            }
        }).catch(() => {
            this.form.patchValue({ logo: null });
        });
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading = true;
        const raw = this.form.getRawValue();
        const companyPayload = {
            name: raw.name || null,
            companyCode: raw.companyCode ?? '',
            logo: raw.logo || null,
            email: raw.email || null,
            phone: raw.phone || null,
            website: raw.website || null,
            country: raw.country || null,
            city: raw.city || null,
            address: raw.address || null,
            timezone: raw.timezone || null,
        };

        lastValueFrom(this._globalService.updateCompany(companyPayload))
            .then(async (resp: any) => {
                this._toast.success(resp?.message || 'Company profile updated successfully');

                const profileResp = await lastValueFrom(this._authService.getProfile());
                const profileData = (profileResp as any)?.data ?? profileResp;
                this._authService.profileData = profileData;
                this._erpPdf.clearCompanyLogoCache();
                if (profileData?.user) {
                    this._userService.user = profileData.user;
                }

                // Refresh company permissions so header modules update
                lastValueFrom(this._authService.refreshCompanyPermissions()).then(() => {
                    const u = this._authService.profile()?.user;
                    if (u) this._userService.user = u;
                }).catch(() => {});

                const companyId = profileData?.company?.id ?? profileData?.company?.Id;
                const rawKeys = (this.form.get('selectedPermissionKeys')?.value as string[]) ?? [];
                const permIds = [
                    ...new Set(
                        rawKeys
                            .map((key) => this.resolvePermissionIdForKey(key))
                            .filter((id): id is string => !!id)
                    ),
                ];

                if (companyId && this.showCompanyPermissions && this.canEditPermissions) {
                    try {
                        await lastValueFrom(this._globalService.replaceCompanyPermissions({
                            companyId: String(companyId),
                            permissionIds: permIds,
                        }));
                        this._toast.success('Company permissions updated successfully');
                    } catch (err: any) {
                        this._toast.warning('Company updated but some permissions could not be assigned');
                    }
                }
                this.loading = false;
                this._dialogRef?.close(true);
            })
            .catch((err: HttpErrorResponse) => {
                this.loading = false;
                this._toast.error(err?.error?.message || err?.error?.title || 'Failed to update company profile');
            });
    }
}
